import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterBar from "@/components/base/FilterBar";
import Pagination from "@/components/base/Pagination";
import FilmCard from "@/components/feature/FilmCard";
import EmptyState from "@/components/feature/EmptyState";
import { films, filmRegions, filmGenres } from "@/mocks/films";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

const sortOptions = [
  { label: "按更新时间", value: "updated" },
  { label: "按热度", value: "heat" },
  { label: "按评分", value: "rating" },
];

const yearMap: Record<string, "all" | "5" | "10"> = {
  全部年份: "all",
  近五年: "5",
  近十年: "10",
};

export default function FilmsCategory() {
  useDocumentMeta({
    title: "影视作品 - 巨乳娱乐资讯网",
    description:
      "巨乳娱乐资讯网影视作品栏目，聚合最新影视剧与电影介绍，支持按地区、类型、年份筛选，并按更新、热度、评分排序浏览。",
    keywords: "影视作品,影视剧介绍,电影推荐,影视资讯,巨乳娱乐资讯网",
  });

  const [region, setRegion] = useState("全部地区");
  const [genre, setGenre] = useState("全部类型");
  const [year, setYear] = useState("全部年份");
  const [sort, setSort] = useState("updated");
  const [page, setPage] = useState(1);

  const nowYear = new Date().getFullYear();

  const filtered = useMemo(() => {
    const yearRule = yearMap[year] ?? "all";
    const list = films.filter((film) => {
      const regionOk = region === "全部地区" || film.region === region;
      const genreOk = genre === "全部类型" || film.genres.includes(genre);
      let yearOk = true;
      if (yearRule === "5") yearOk = film.year >= nowYear - 4;
      if (yearRule === "10") yearOk = film.year >= nowYear - 9;
      return regionOk && genreOk && yearOk;
    });

    return [...list].sort((a, b) => {
      if (sort === "heat") return b.heat - a.heat;
      if (sort === "rating") return b.rating - a.rating;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [region, genre, year, sort, nowYear]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handleFilter = (key: string, value: string) => {
    setPage(1);
    if (key === "region") setRegion(value);
    if (key === "genre") setGenre(value);
    if (key === "year") setYear(value);
  };

  const resetAll = () => {
    setRegion("全部地区");
    setGenre("全部类型");
    setYear("全部年份");
    setSort("updated");
    setPage(1);
  };

  const groups = [
    { key: "region", label: "地区", options: ["全部地区", ...filmRegions], value: region },
    { key: "genre", label: "类型", options: ["全部类型", ...filmGenres], value: genre },
    { key: "year", label: "年份", options: ["全部年份", "近五年", "近十年"], value: year },
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视作品" }]} />

      <div className="mt-4 flex flex-col gap-2">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          影视作品
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-foreground-600">
          收录影视剧与电影的详细介绍，涵盖剧情、喜剧、动作、科幻、悬疑等多元类型。
          你可以按地区、类型与年份筛选，并按更新时间、热度或评分排序，快速找到想看的作品。
        </p>
      </div>

      <div className="mt-5">
        <FilterBar groups={groups} onChange={handleFilter} />
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-foreground-500">
          共 <strong className="text-primary-600">{filtered.length}</strong> 部作品
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-500">排序</span>
          <div className="flex gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setSort(option.value);
                  setPage(1);
                }}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors cursor-pointer ${
                  sort === option.value
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {pageItems.length > 0 ? (
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3">
          {pageItems.map((film) => (
            <FilmCard key={film.id} film={film} />
          ))}
        </div>
      ) : (
        <div className="mt-5">
          <EmptyState
            title="没有符合条件的影视作品"
            description="试试更换筛选条件，或重置全部条件查看所有作品。"
            actionLabel="重置筛选条件"
            onAction={resetAll}
          />
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}